<template>
  <div class="orders-view">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>
    <div class="top-spacer"></div>

    <div class="panel-head">
      <div class="head-left">
        <h3>Maestro de recibos de empleado</h3>
      </div>
      <div class="head-actions">
        <button v-if="canCreateReceipt" type="button" class="btn-primary" @click="openCreateReceiptModal">
          Crear nuevo recibo
        </button>
        <button v-if="canCreateReceipt" type="button" class="btn-secondary" @click="openImportModal">
          Importar archivo
        </button>
        <img src="@/assets/filtros.png" alt="filtros" class="head-icon" />
      </div>
    </div>

    <form class="filters-card" @submit.prevent="queryReceipts">
      <div class="row">
        <label for="employee_ident">Empleado (ID)</label>
        <input id="employee_ident" type="text" v-model="employeeIdent" placeholder="Ej: 1234" />
        <div class="cell span-2"></div>
      </div>

      <div class="row">
        <label for="employee_name">Empleado (Nombre)</label>
        <input id="employee_name" type="text" v-model="employeeName" placeholder="Nombre o apellidos" />
        <div class="cell span-2"></div>
      </div>

      <div class="row">
        <label for="concept_description">Concepto</label>
        <select id="concept_description" v-model="conceptDescription">
          <option value="">Todos</option>
          <option v-for="option in conceptOptions" :key="option" :value="option">{{ option }}</option>
        </select>
        <div class="cell span-2"></div>
      </div>

      <div class="row">
        <label for="approved_by">Aprobado por</label>
        <input id="approved_by" type="text" v-model="approvedBy" placeholder="Nombre o ID de aprobador" />
        <div class="cell span-2"></div>
      </div>

      <div class="row">
        <label for="unit">Unidad</label>
        <input id="unit" type="text" v-model="unitShortId" placeholder="# Económico" />
        <div class="cell span-2"></div>
      </div>

      <div class="row">
        <label>Monto</label>
        <input type="number" step="0.01" inputmode="decimal" v-model="amountMin" placeholder="Minimo" />
        <span class="sep">al</span>
        <input type="number" step="0.01" inputmode="decimal" v-model="amountMax" placeholder="Maximo" />
      </div>

      <div class="row">
        <label>Fecha de creación</label>
        <input type="datetime-local" v-model="requestTimeStart" />
        <span class="sep">al</span>
        <input type="datetime-local" v-model="requestTimeEnd" />
      </div>

      <div class="row">
        <label>Fecha de aplicación</label>
        <input type="datetime-local" v-model="effectiveDateStart" />
        <span class="sep">al</span>
        <input type="datetime-local" v-model="effectiveDateEnd" />
      </div>

      <div class="row">
        <label for="receipt_status">Estatus</label>
        <select id="receipt_status" v-model="receiptStatus">
          <option value="">Todos</option>
          <option value="active">Activos</option>
          <option value="cancelled">Cancelados</option>
        </select>
        <div class="cell span-2"></div>
      </div>

      <div class="row actions">
        <div class="cell span-4">
          <button type="submit" class="btn-primary">Aplicar filtros</button>
        </div>
      </div>
    </form>

    <section class="results-card" id="results-section">
      <div v-if="!canViewTable" class="no-access">No tienes permisos para ver el listado de recibos.</div>
      <div class="results-head">
        <h2>Resultados: {{ totalCount }}</h2>
      </div>

      <div v-if="canViewTable" class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th @click="toggleSort('id')">Recibo # <span v-if="isSorted('id')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('request_time')">Creación <span v-if="isSorted('request_time')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('effective_date')">Aplicación <span v-if="isSorted('effective_date')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('employee_ident')">Empleado <span v-if="isSorted('employee_ident')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('concept_description')">Concepto <span v-if="isSorted('concept_description')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('unit_short_id')">Unidad <span v-if="isSorted('unit_short_id')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('approved_by')">Aprobado por <span v-if="isSorted('approved_by')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th @click="toggleSort('amount')">Monto <span v-if="isSorted('amount')">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span><span v-else>↕</span></th>
              <th>Estatus</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="receipt in queriedReceipts" :key="receipt.id" class="row-click" :class="{ 'row-cancelled': receipt.is_cancelled }" @click="openReceiptDetails(receipt)">
              <td>#{{ receipt.id }}</td>
              <td>{{ formatDateTime(receipt.request_time) }}</td>
              <td>{{ receipt.effective_date ? formatDateTime(receipt.effective_date) : 'N/A' }}</td>
              <td>{{ receipt.employee_ident ? `${receipt.employee_ident} - ${receipt.employee_name || ''}` : 'Sin empleado' }}</td>
              <td>{{ receipt.concept_description || 'N/A' }}</td>
              <td>{{ receipt.unit_short_id || 'Sin unidad' }}</td>
              <td>{{ receipt.approved_by_ident ? `${receipt.approved_by_ident} - ${receipt.approved_by_name || ''}` : 'Sin aprobador' }}</td>
              <td>{{ formatMoney(receipt.amount) }}</td>
              <td>
                <span :class="['status-pill', receipt.is_cancelled ? 'status-cancelled' : 'status-active']">
                  {{ receipt.is_cancelled ? 'Cancelado' : 'Activo' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pg-btn" aria-label="Página anterior">
          <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
        </button>
        <span v-for="pageNumber in totalPages" :key="pageNumber" @click="goToPage(pageNumber)" :class="['pg-num', { active: pageNumber === currentPage }]">
          {{ pageNumber }}
        </span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pg-btn" aria-label="Página siguiente">
          <img src="@/assets/angulo-derecho.png" alt="Next Page" />
        </button>
      </div>
    </section>

    <div v-if="showReceiptDetailsModal && selectedReceipt" class="modal-backdrop" @click.self="closeReceiptDetailsModal">
      <div class="modal-card">
        <div class="modal-head">
          <h4>Recibo #{{ selectedReceipt.id }}</h4>
          <button type="button" class="btn-close" @click="closeReceiptDetailsModal">X</button>
        </div>
        <div class="modal-body">
          <div v-if="selectedReceipt.is_cancelled" class="cancelled-banner">
            <strong>Recibo cancelado</strong>
            <span v-if="selectedReceipt.cancelled_at"> el {{ formatDateTime(selectedReceipt.cancelled_at) }}</span>
            <span v-if="selectedReceipt.cancelled_by_name"> por {{ selectedReceipt.cancelled_by_name }}</span>.
            <span v-if="selectedReceipt.cancellation_reason"> Motivo: {{ selectedReceipt.cancellation_reason }}</span>
            <br />No cuenta para el cálculo de nómina.
          </div>
          <div class="receipt-grid">
            <div class="field">
              <label>Fecha de creación</label>
              <input type="text" :value="formatDateTime(selectedReceipt.request_time)" disabled />
            </div>
            <div class="field">
              <label>Fecha de aplicación</label>
              <input v-if="isEditingReceipt" type="datetime-local" v-model="editReceiptDraft.effective_date" />
              <input v-else type="text" :value="selectedReceipt.effective_date ? formatDateTime(selectedReceipt.effective_date) : 'N/A'" disabled />
            </div>
            <div class="field">
              <label>Empleado</label>
              <div v-if="isEditingReceipt" class="employee-search-wrap">
                <input
                  type="text"
                  v-model="editEmployeeSearchTerm"
                  placeholder="Buscar por número o nombre"
                  @input="searchEmployees('edit_employee')"
                  autocomplete="off"
                />
                <ul v-if="editReceiptEmployeeSuggestions.length" class="suggestions suggestions-employee">
                  <li v-for="employee in editReceiptEmployeeSuggestions" :key="`edit-employee-${employee.employee_ident}`" @click="selectEditEmployee(employee)">
                    {{ employee.display_name }}
                  </li>
                </ul>
                <small v-if="editReceiptDraft.employee" class="selected-employee">
                  Seleccionado: {{ editReceiptDraft.employee }} - {{ editReceiptDraft.employee_name || "Sin nombre" }}
                </small>
              </div>
              <input v-else type="text" :value="selectedReceipt.employee_ident ? `${selectedReceipt.employee_ident} - ${selectedReceipt.employee_name || ''}` : 'Sin empleado'" disabled />
            </div>
            <div class="field">
              <label>Aprobado por</label>
              <div v-if="isEditingReceipt" class="employee-search-wrap">
                <input
                  type="text"
                  v-model="editApproverSearchTerm"
                  placeholder="Buscar por número o nombre"
                  @input="searchEmployees('edit_approved_by')"
                  autocomplete="off"
                />
                <ul v-if="editReceiptApproverSuggestions.length" class="suggestions suggestions-employee">
                  <li v-for="employee in editReceiptApproverSuggestions" :key="`edit-approved-by-${employee.employee_ident}`" @click="selectEditApprover(employee)">
                    {{ employee.display_name }}
                  </li>
                </ul>
                <small v-if="editReceiptDraft.approved_by" class="selected-employee">
                  Seleccionado: {{ editReceiptDraft.approved_by }} - {{ editReceiptDraft.approved_by_name || "Sin nombre" }}
                </small>
              </div>
              <input v-else type="text" :value="selectedReceipt.approved_by_ident ? `${selectedReceipt.approved_by_ident} - ${selectedReceipt.approved_by_name || ''}` : 'Sin aprobador'" disabled />
            </div>
            <div class="field">
              <label>Monto *</label>
              <input v-if="isEditingReceipt" type="number" step="0.01" inputmode="decimal" v-model="editReceiptDraft.amount" />
              <input v-else type="text" :value="formatMoney(selectedReceipt.amount)" disabled />
            </div>
            <div class="field">
              <label>Unidad</label>
              <select v-if="isEditingReceipt" v-model="editReceiptDraft.unit">
                <option :value="null">Selecciona unidad</option>
                <option v-for="unit in receiptUnits" :key="`edit-unit-${unit.id}`" :value="unit.id">
                  {{ unit.unit_short_id }} - {{ unit.unit_sub_brand }}
                </option>
              </select>
              <input v-else type="text" :value="selectedReceipt.unit_short_id ? `${selectedReceipt.unit_short_id} - ${selectedReceipt.unit_sub_brand || ''}` : 'Sin unidad'" disabled />
            </div>
            <div class="field field-full">
              <label>Concepto *</label>
              <select v-if="isEditingReceipt" v-model="editReceiptDraft.concept_description">
                <option value="" disabled>Selecciona un concepto</option>
                <option v-for="option in conceptOptions" :key="option" :value="option">{{ option }}</option>
              </select>
              <textarea v-else :value="selectedReceipt.concept_description || '-'" disabled></textarea>
            </div>
            <div class="field field-full">
              <label>Observaciones</label>
              <textarea v-if="isEditingReceipt" v-model.trim="editReceiptDraft.observations" placeholder="Observaciones adicionales"></textarea>
              <textarea v-else :value="selectedReceipt.observations || '-'" disabled></textarea>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <template v-if="isEditingReceipt">
            <button type="button" class="btn-secondary" @click="cancelEditReceipt" :disabled="savingReceiptEdits">Cancelar</button>
            <button type="button" class="btn-primary" @click="saveReceiptEdits" :disabled="savingReceiptEdits">
              {{ savingReceiptEdits ? 'Guardando...' : 'Guardar cambios' }}
            </button>
          </template>
          <template v-else>
            <button v-if="canEditReceipt && !selectedReceipt.is_cancelled" type="button" class="btn-secondary" @click="startEditReceipt(selectedReceipt)">
              Editar
            </button>
            <button v-if="canEditReceipt && !selectedReceipt.is_cancelled" type="button" class="btn-danger" @click="cancelReceipt(selectedReceipt)" :disabled="cancellingReceipt">
              {{ cancellingReceipt ? 'Procesando...' : 'Cancelar recibo' }}
            </button>
            <button v-if="canEditReceipt && selectedReceipt.is_cancelled" type="button" class="btn-secondary" @click="restoreReceipt(selectedReceipt)" :disabled="cancellingReceipt">
              {{ cancellingReceipt ? 'Procesando...' : 'Restaurar recibo' }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <div v-if="showCreateReceiptModal" class="modal-backdrop" @click.self="closeCreateReceiptModal">
      <div class="modal-card modal-card-lg">
        <div class="modal-head">
          <h4>Crear nuevo recibo</h4>
          <button type="button" class="btn-close" @click="closeCreateReceiptModal">X</button>
        </div>
        <div class="modal-body">
          <div class="receipt-grid">
            <div class="field">
              <label>Fecha de creación</label>
              <input type="text" :value="new Date().toLocaleString('es-MX')" disabled />
            </div>
            <div class="field">
              <label>Fecha de aplicación</label>
              <input type="datetime-local" v-model="receiptDraft.effective_date" />
            </div>
            <div class="field field-full employee-search-wrap">
              <label>Empleado</label>
              <input
                type="text"
                v-model="employeeSearchTerm"
                placeholder="Buscar por número o nombre"
                @input="searchEmployees('employee')"
                autocomplete="off"
              />
              <ul v-if="receiptEmployeeSuggestions.length" class="suggestions suggestions-employee">
                <li v-for="employee in receiptEmployeeSuggestions" :key="`new-employee-${employee.employee_ident}`" @click="selectEmployee(employee)">
                  {{ employee.display_name }}
                </li>
              </ul>
              <small v-if="receiptDraft.employee_ident" class="selected-employee">
                Seleccionado: {{ receiptDraft.employee_ident }} - {{ receiptDraft.employee_name || "Sin nombre" }}
              </small>
            </div>
            <div class="field field-full employee-search-wrap">
              <label>Aprobado por</label>
              <input
                type="text"
                v-model="approvedBySearchTerm"
                placeholder="Buscar por número o nombre"
                @input="searchEmployees('approved_by')"
                autocomplete="off"
              />
              <ul v-if="receiptApproverSuggestions.length" class="suggestions suggestions-employee">
                <li v-for="employee in receiptApproverSuggestions" :key="`new-approver-${employee.employee_ident}`" @click="selectApprover(employee)">
                  {{ employee.display_name }}
                </li>
              </ul>
              <small v-if="receiptDraft.approved_by_ident" class="selected-employee">
                Seleccionado: {{ receiptDraft.approved_by_ident }} - {{ receiptDraft.approved_by_name || "Sin nombre" }}
              </small>
            </div>
            <div class="field">
              <label>Monto *</label>
              <input type="number" step="0.01" inputmode="decimal" v-model="receiptDraft.amount" />
            </div>
            <div class="field">
              <label>Unidad</label>
              <select v-model="receiptDraft.unit">
                <option :value="null">Selecciona unidad</option>
                <option v-for="unit in receiptUnits" :key="unit.id" :value="unit.id">
                  {{ unit.unit_short_id }} - {{ unit.unit_sub_brand }}
                </option>
              </select>
            </div>
            <div class="field field-full">
              <label>Concepto *</label>
              <select v-model="receiptDraft.concept_description">
                <option value="" disabled>Selecciona un concepto</option>
                <option v-for="option in conceptOptions" :key="option" :value="option">{{ option }}</option>
              </select>
            </div>
            <div class="field field-full">
              <label>Observaciones</label>
              <textarea v-model.trim="receiptDraft.observations" placeholder="Observaciones adicionales"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="closeCreateReceiptModal" :disabled="savingReceipt">Cancelar</button>
          <button type="button" class="btn-primary" @click="createReceiptFromMaster" :disabled="savingReceipt">
            {{ savingReceipt ? "Guardando..." : "Guardar recibo" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showImportModal" class="modal-backdrop" @click.self="closeImportModal">
      <div class="modal-card modal-card-lg">
        <div class="modal-head">
          <h4>Importar recibos desde archivo</h4>
          <button type="button" class="btn-close" @click="closeImportModal">X</button>
        </div>
        <div class="modal-body">
          <div class="import-disclaimer">
            <strong>Formato del archivo (.csv o .xlsx):</strong>
            columnas <code>empleado</code>, <code>concepto</code>, <code>unidad</code>, <code>monto</code>, <code>observaciones</code>.
            <br />
            Campos <span class="mandatory-text">obligatorios</span>: <strong>empleado</strong>, <strong>concepto</strong> y <strong>monto</strong>.
            Campos opcionales: unidad y observaciones.
            <br />
            <span class="mandatory-text">El número de empleado</span> (y la unidad, si se indica) deben existir en el sistema.
          </div>

          <div class="import-field-row">
            <label for="import-effective-date">Seleccionar fecha de aplicación</label>
            <input id="import-effective-date" type="datetime-local" v-model="importEffectiveDate" />
            <span class="import-hint">Se aplicará a todos los recibos de esta carga.</span>
          </div>

          <div class="import-file-row">
            <input ref="importFileInput" type="file" accept=".csv,.xlsx,.xlsm" @change="onImportFileChange" />
            <span v-if="parsingImport" class="import-status">Leyendo archivo...</span>
          </div>

          <div v-if="importRows.length" class="import-summary">
            <span class="badge badge-ok">Válidas: {{ validImportCount }}</span>
            <span class="badge badge-bad">Con errores: {{ invalidImportCount }}</span>
            <span class="import-hint">Puedes editar cualquier celda directamente. Las filas en rojo tienen campos obligatorios faltantes.</span>
          </div>

          <div v-if="importRows.length" class="table-scroll import-preview">
            <table class="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Empleado *</th>
                  <th>Concepto *</th>
                  <th>Unidad</th>
                  <th>Monto *</th>
                  <th>Observaciones</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in importRows" :key="row.clientId" :class="rowIsValid(row) ? 'row-valid' : 'row-invalid'">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <input type="text" v-model.trim="row.empleado" :class="{ 'cell-error': !row.empleado || row.employeeExists === false }" @input="scheduleImportValidation" :title="row.employeeExists === false ? 'Este empleado no existe' : ''" />
                    <small v-if="row.empleado && row.employeeExists === false" class="cell-note">No existe</small>
                  </td>
                  <td><input type="text" v-model.trim="row.concepto" :class="{ 'cell-error': !row.concepto }" /></td>
                  <td>
                    <input type="text" v-model.trim="row.unidad" :class="{ 'cell-error': row.unidad && row.unitExists === false }" @input="scheduleImportValidation" :title="row.unidad && row.unitExists === false ? 'Esta unidad no existe' : ''" />
                    <small v-if="row.unidad && row.unitExists === false" class="cell-note">No existe</small>
                  </td>
                  <td><input type="text" v-model.trim="row.monto" :class="{ 'cell-error': !isValidAmount(row.monto) }" /></td>
                  <td><input type="text" v-model.trim="row.observaciones" /></td>
                  <td><button type="button" class="btn-remove-row" @click="removeImportRow(row.clientId)" title="Eliminar fila">X</button></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="!parsingImport" class="import-empty">
            Selecciona un archivo para ver la vista previa.
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="closeImportModal" :disabled="importing">Cancelar</button>
          <button type="button" class="btn-primary" @click="saveImport" :disabled="importing || !importRows.length">
            {{ importing ? "Importando..." : `Importar ${validImportCount} recibo(s)` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "EmployeeReceiptsFilters",
  data() {
    return {
      permissions: [],
      conceptOptions: [
        "Caja Monterrey",
        "Caja Saltillo",
        "Servicios OT",
        "Herramienta",
        "Uniformes",
        "Comedor",
        "Abono Préstamo",
        "Pensión",
        "Apoyo Defunción",
        "Apoyo Sindical",
        "Otro",
      ],
      employeeIdent: "",
      employeeName: "",
      conceptDescription: "",
      approvedBy: "",
      unitShortId: "",
      amountMin: "",
      amountMax: "",
      requestTimeStart: "",
      requestTimeEnd: "",
      effectiveDateStart: "",
      effectiveDateEnd: "",
      receiptStatus: "",
      cancellingReceipt: false,
      queriedReceipts: [],
      showImportModal: false,
      parsingImport: false,
      importing: false,
      importRows: [],
      importClientIdCounter: 0,
      importValidationTimer: null,
      importEffectiveDate: "",
      selectedReceipt: null,
      showReceiptDetailsModal: false,
      showCreateReceiptModal: false,
      savingReceipt: false,
      isEditingReceipt: false,
      savingReceiptEdits: false,
      editReceiptDraft: {
        employee: "",
        employee_name: "",
        approved_by: "",
        approved_by_name: "",
        amount: "",
        unit: null,
        concept_description: "",
        observations: "",
        effective_date: "",
      },
      receiptUnits: [],
      employeeSearchTerm: "",
      approvedBySearchTerm: "",
      editEmployeeSearchTerm: "",
      editApproverSearchTerm: "",
      receiptEmployeeSuggestions: [],
      receiptApproverSuggestions: [],
      editReceiptEmployeeSuggestions: [],
      editReceiptApproverSuggestions: [],
      receiptEmployeeSearchTimer: null,
      receiptApproverSearchTimer: null,
      editReceiptEmployeeSearchTimer: null,
      editReceiptApproverSearchTimer: null,
      receiptDraft: {
        employee_ident: "",
        employee_name: "",
        approved_by_ident: "",
        approved_by_name: "",
        amount: "",
        concept_description: "",
        unit: null,
        observations: "",
        effective_date: "",
      },
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      sortState: { by: "request_time", dir: "desc" },
    };
  },
  computed: {
    canViewMaster() {
      return this.hasAnyPerm([
        "access_control.ui_access_rrhh_receipts",
        "ui_access_rrhh_receipts",
      ]);
    },
    canViewTable() {
      return this.hasAnyPerm([
        "access_control.ui_access_rrhh_receipts_table",
        "ui_access_rrhh_receipts_table",
      ]);
    },
    canCreateReceipt() {
      return this.hasAnyPerm([
        "access_control.ui_access_rrhh_receipts_create",
        "ui_access_rrhh_receipts_create",
      ]);
    },
    canEditReceipt() {
      return this.canCreateReceipt;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
    validImportCount() {
      return this.importRows.filter((row) => this.rowIsValid(row)).length;
    },
    invalidImportCount() {
      return this.importRows.length - this.validImportCount;
    },
  },
  mounted() {
    this.loadPermissions();
    if (!this.canViewMaster) {
      Swal.fire("Sin acceso", "No tienes permisos para ver el maestro de recibos.", "warning");
      this.$router.push({ name: "MainPanel" });
      return;
    }
    this.fetchReceiptUnits();
  },
  beforeUnmount() {
    if (this.receiptEmployeeSearchTimer) clearTimeout(this.receiptEmployeeSearchTimer);
    if (this.receiptApproverSearchTimer) clearTimeout(this.receiptApproverSearchTimer);
    if (this.editReceiptEmployeeSearchTimer) clearTimeout(this.editReceiptEmployeeSearchTimer);
    if (this.editReceiptApproverSearchTimer) clearTimeout(this.editReceiptApproverSearchTimer);
    if (this.importValidationTimer) clearTimeout(this.importValidationTimer);
  },
  methods: {
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem("userPermissions")) || [];
      } catch (error) {
        this.permissions = [];
      }
    },
    hasPerm(perm) {
      if (!perm) return false;
      if (this.permissions.includes(perm)) return true;
      return this.permissions.includes(`access_control.${perm}`);
    },
    hasAnyPerm(perms) {
      if (!Array.isArray(perms) || !perms.length) return false;
      return perms.some((perm) => this.hasPerm(perm));
    },
    backMainPanel() {
      this.$router.push({ path: "/main-panel" });
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) return String(value);
      return parsedDate.toLocaleString("es-MX");
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "N/A";
      const parsed = Number(value);
      if (Number.isNaN(parsed)) return String(value);
      return parsed.toFixed(2);
    },
    orderingParam() {
      if (!this.sortState?.by) return "";
      const prefix = this.sortState.dir === "desc" ? "-" : "";
      return `&ordering=${encodeURIComponent(prefix + this.sortState.by)}`;
    },
    isSorted(field) {
      return this.sortState.by === field;
    },
    toggleSort(field) {
      if (this.sortState.by === field) {
        this.sortState.dir = this.sortState.dir === "asc" ? "desc" : "asc";
      } else {
        this.sortState.by = field;
        this.sortState.dir = "asc";
      }
      this.currentPage = 1;
      this.queryReceipts();
    },
    goToPage(pageNumber) {
      this.currentPage = pageNumber;
      this.queryReceipts();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1;
      this.queryReceipts();
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage -= 1;
      this.queryReceipts();
    },
    openReceiptDetails(receipt) {
      if (!this.canViewTable) {
        Swal.fire("Sin acceso", "No tienes permisos para ver el detalle de recibos.", "warning");
        return;
      }
      this.selectedReceipt = receipt;
      this.isEditingReceipt = false;
      this.showReceiptDetailsModal = true;
    },
    closeReceiptDetailsModal() {
      this.showReceiptDetailsModal = false;
      this.selectedReceipt = null;
      this.isEditingReceipt = false;
    },
    startEditReceipt(receipt) {
      if (!this.canEditReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para editar recibos.", "warning");
        return;
      }
      this.editReceiptDraft = {
        employee: receipt.employee_ident || "",
        employee_name: receipt.employee_name || "",
        approved_by: receipt.approved_by_ident || "",
        approved_by_name: receipt.approved_by_name || "",
        amount: receipt.amount ?? "",
        unit: receipt.unit ?? null,
        concept_description: receipt.concept_description || "",
        observations: receipt.observations || "",
        effective_date: this.toLocalDateTimeInput(receipt.effective_date),
      };
      this.editEmployeeSearchTerm = receipt.employee_ident ? `${receipt.employee_ident} - ${receipt.employee_name || ""}` : "";
      this.editApproverSearchTerm = receipt.approved_by_ident ? `${receipt.approved_by_ident} - ${receipt.approved_by_name || ""}` : "";
      this.editReceiptEmployeeSuggestions = [];
      this.editReceiptApproverSuggestions = [];
      this.isEditingReceipt = true;
    },
    cancelEditReceipt() {
      this.isEditingReceipt = false;
    },
    async saveReceiptEdits() {
      if (!this.canEditReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para editar recibos.", "warning");
        return;
      }
      if (!this.selectedReceipt?.id) return;
      if (!this.editReceiptDraft.amount || !this.editReceiptDraft.concept_description) {
        Swal.fire("Campos obligatorios", "Completa monto y concepto.", "warning");
        return;
      }
      try {
        const payload = {
          employee: this.editReceiptDraft.employee || null,
          approved_by: this.editReceiptDraft.approved_by || null,
          amount: this.normalizeDecimalValue(this.editReceiptDraft.amount),
          unit: this.editReceiptDraft.unit || null,
          concept_description: this.editReceiptDraft.concept_description,
          observations: this.normalizeTextValue(this.editReceiptDraft.observations),
          effective_date: this.editReceiptDraft.effective_date || null,
        };
        const apiDirectory = process.env.API_DIRECTORY;
        this.savingReceiptEdits = true;
        const response = await axios.put(`${apiDirectory}/employee-receipt/${this.selectedReceipt.id}/`, payload);
        const updated = response.data || {};
        this.selectedReceipt = { ...this.selectedReceipt, ...updated };
        const index = this.queriedReceipts.findIndex((item) => item.id === this.selectedReceipt.id);
        if (index !== -1) {
          this.queriedReceipts.splice(index, 1, { ...this.queriedReceipts[index], ...updated });
        }
        this.isEditingReceipt = false;
        Swal.fire("Guardado", "El recibo se actualizó correctamente.", "success");
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire("Error", errorMessage || "No se pudo actualizar el recibo.", "error");
      } finally {
        this.savingReceiptEdits = false;
      }
    },
    applyReceiptUpdateLocally(updated) {
      if (!updated || !this.selectedReceipt) return;
      this.selectedReceipt = { ...this.selectedReceipt, ...updated };
      const index = this.queriedReceipts.findIndex((item) => item.id === updated.id);
      if (index !== -1) {
        this.queriedReceipts.splice(index, 1, { ...this.queriedReceipts[index], ...updated });
      }
    },
    async cancelReceipt(receipt) {
      if (!this.canEditReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para cancelar recibos.", "warning");
        return;
      }
      if (!receipt?.id) return;
      const result = await Swal.fire({
        title: "Cancelar recibo",
        text: "El recibo dejará de contar para el cálculo de nómina. ¿Deseas continuar?",
        input: "textarea",
        inputLabel: "Motivo (opcional)",
        inputPlaceholder: "Motivo de la cancelación",
        showCancelButton: true,
        confirmButtonText: "Sí, cancelar",
        cancelButtonText: "No",
        icon: "warning",
      });
      if (!result.isConfirmed) return;
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        this.cancellingReceipt = true;
        const response = await axios.post(`${apiDirectory}/employee-receipt/${receipt.id}/cancel/`, {
          cancel: true,
          cancellation_reason: result.value || null,
        });
        this.applyReceiptUpdateLocally(response.data || {});
        Swal.fire("Recibo cancelado", "El recibo fue cancelado y ya no cuenta para nómina.", "success");
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire("Error", errorMessage || "No se pudo cancelar el recibo.", "error");
      } finally {
        this.cancellingReceipt = false;
      }
    },
    async restoreReceipt(receipt) {
      if (!this.canEditReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para restaurar recibos.", "warning");
        return;
      }
      if (!receipt?.id) return;
      const result = await Swal.fire({
        title: "Restaurar recibo",
        text: "El recibo volverá a contar para el cálculo de nómina. ¿Deseas continuar?",
        showCancelButton: true,
        confirmButtonText: "Sí, restaurar",
        cancelButtonText: "No",
        icon: "question",
      });
      if (!result.isConfirmed) return;
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        this.cancellingReceipt = true;
        const response = await axios.post(`${apiDirectory}/employee-receipt/${receipt.id}/cancel/`, {
          cancel: false,
        });
        this.applyReceiptUpdateLocally(response.data || {});
        Swal.fire("Recibo restaurado", "El recibo fue restaurado correctamente.", "success");
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire("Error", errorMessage || "No se pudo restaurar el recibo.", "error");
      } finally {
        this.cancellingReceipt = false;
      }
    },
    nowLocalDateTime() {
      const now = new Date();
      const offsetMs = now.getTimezoneOffset() * 60000;
      return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    toLocalDateTimeInput(value) {
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      const offsetMs = parsed.getTimezoneOffset() * 60000;
      return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    resetReceiptDraft() {
      this.receiptDraft = {
        employee_ident: "",
        employee_name: "",
        approved_by_ident: "",
        approved_by_name: "",
        amount: "",
        concept_description: "",
        unit: null,
        observations: "",
        effective_date: this.nowLocalDateTime(),
      };
      this.employeeSearchTerm = "";
      this.approvedBySearchTerm = "";
      this.receiptEmployeeSuggestions = [];
      this.receiptApproverSuggestions = [];
      this.editEmployeeSearchTerm = "";
      this.editApproverSearchTerm = "";
      this.editReceiptEmployeeSuggestions = [];
      this.editReceiptApproverSuggestions = [];
    },
    openCreateReceiptModal() {
      if (!this.canCreateReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para crear recibos.", "warning");
        return;
      }
      this.resetReceiptDraft();
      this.showCreateReceiptModal = true;
    },
    closeCreateReceiptModal() {
      this.showCreateReceiptModal = false;
      this.resetReceiptDraft();
    },
    normalizeDecimalValue(value) {
      if (value === null || value === undefined || value === "") return null;
      return value;
    },
    normalizeTextValue(value) {
      if (value === null || value === undefined || value === "") return null;
      return String(value).trim();
    },
    async fetchReceiptUnits() {
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.get(`${apiDirectory}/employee-receipt-unit-options/`);
        this.receiptUnits = Array.isArray(response.data?.results) ? response.data.results : [];
      } catch (error) {
        this.receiptUnits = [];
      }
    },
    searchEmployees(target) {
      const searchTermMap = {
        employee: this.employeeSearchTerm,
        approved_by: this.approvedBySearchTerm,
        edit_employee: this.editEmployeeSearchTerm,
        edit_approved_by: this.editApproverSearchTerm,
      };
      const search = String(searchTermMap[target] || "").trim();
      if (target === "employee") {
        this.receiptDraft.employee_ident = "";
        this.receiptDraft.employee_name = "";
      } else if (target === "approved_by") {
        this.receiptDraft.approved_by_ident = "";
        this.receiptDraft.approved_by_name = "";
      } else if (target === "edit_employee") {
        this.editReceiptDraft.employee = "";
        this.editReceiptDraft.employee_name = "";
      } else if (target === "edit_approved_by") {
        this.editReceiptDraft.approved_by = "";
        this.editReceiptDraft.approved_by_name = "";
      }
      if (target === "employee" && this.receiptEmployeeSearchTimer) clearTimeout(this.receiptEmployeeSearchTimer);
      if (target === "approved_by" && this.receiptApproverSearchTimer) clearTimeout(this.receiptApproverSearchTimer);
      if (target === "edit_employee" && this.editReceiptEmployeeSearchTimer) clearTimeout(this.editReceiptEmployeeSearchTimer);
      if (target === "edit_approved_by" && this.editReceiptApproverSearchTimer) clearTimeout(this.editReceiptApproverSearchTimer);
      if (search.length < 2) {
        if (target === "employee") this.receiptEmployeeSuggestions = [];
        else if (target === "approved_by") this.receiptApproverSuggestions = [];
        else if (target === "edit_employee") this.editReceiptEmployeeSuggestions = [];
        else this.editReceiptApproverSuggestions = [];
        return;
      }
      if (target === "employee") {
        this.receiptEmployeeSearchTimer = setTimeout(() => {
          this.fetchEmployeeSuggestions(search, target);
        }, 250);
      } else if (target === "approved_by") {
        this.receiptApproverSearchTimer = setTimeout(() => {
          this.fetchEmployeeSuggestions(search, target);
        }, 250);
      } else if (target === "edit_employee") {
        this.editReceiptEmployeeSearchTimer = setTimeout(() => {
          this.fetchEmployeeSuggestions(search, target);
        }, 250);
      } else {
        this.editReceiptApproverSearchTimer = setTimeout(() => {
          this.fetchEmployeeSuggestions(search, target);
        }, 250);
      }
    },
    async fetchEmployeeSuggestions(search, target) {
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.get(`${apiDirectory}/employee-receipt-employee-options/`, {
          params: { q: search },
        });
        const suggestions = Array.isArray(response.data?.results) ? response.data.results : [];
        if (target === "employee") this.receiptEmployeeSuggestions = suggestions;
        else if (target === "approved_by") this.receiptApproverSuggestions = suggestions;
        else if (target === "edit_employee") this.editReceiptEmployeeSuggestions = suggestions;
        else this.editReceiptApproverSuggestions = suggestions;
      } catch (error) {
        if (target === "employee") this.receiptEmployeeSuggestions = [];
        else if (target === "approved_by") this.receiptApproverSuggestions = [];
        else if (target === "edit_employee") this.editReceiptEmployeeSuggestions = [];
        else this.editReceiptApproverSuggestions = [];
      }
    },
    selectEmployee(employee) {
      this.receiptDraft.employee_ident = employee.employee_ident;
      this.receiptDraft.employee_name = employee.full_name || "";
      this.employeeSearchTerm = employee.display_name;
      this.receiptEmployeeSuggestions = [];
    },
    selectApprover(employee) {
      this.receiptDraft.approved_by_ident = employee.employee_ident;
      this.receiptDraft.approved_by_name = employee.full_name || "";
      this.approvedBySearchTerm = employee.display_name;
      this.receiptApproverSuggestions = [];
    },
    selectEditEmployee(employee) {
      this.editReceiptDraft.employee = employee.employee_ident;
      this.editReceiptDraft.employee_name = employee.full_name || "";
      this.editEmployeeSearchTerm = employee.display_name;
      this.editReceiptEmployeeSuggestions = [];
    },
    selectEditApprover(employee) {
      this.editReceiptDraft.approved_by = employee.employee_ident;
      this.editReceiptDraft.approved_by_name = employee.full_name || "";
      this.editApproverSearchTerm = employee.display_name;
      this.editReceiptApproverSuggestions = [];
    },
    async createReceiptFromMaster() {
      try {
        if (!this.canCreateReceipt) {
          Swal.fire("Sin acceso", "No tienes permisos para crear recibos.", "warning");
          return;
        }
        if (!this.receiptDraft.amount || !this.receiptDraft.concept_description) {
          Swal.fire("Campos obligatorios", "Completa monto y concepto.", "warning");
          return;
        }
        const payload = {
          amount: this.normalizeDecimalValue(this.receiptDraft.amount),
          concept_description: this.receiptDraft.concept_description,
          employee: this.receiptDraft.employee_ident || null,
          unit: this.receiptDraft.unit || null,
          approved_by: this.receiptDraft.approved_by_ident || null,
          observations: this.normalizeTextValue(this.receiptDraft.observations),
          effective_date: this.receiptDraft.effective_date || null,
        };
        const apiDirectory = process.env.API_DIRECTORY;
        this.savingReceipt = true;
        await axios.post(`${apiDirectory}/employee-receipts/`, payload);
        this.closeCreateReceiptModal();
        Swal.fire("Guardado", "El recibo se registró correctamente.", "success");
        this.currentPage = 1;
        await this.queryReceipts();
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire("Error", errorMessage || "No se pudo registrar el recibo.", "error");
      } finally {
        this.savingReceipt = false;
      }
    },
    openImportModal() {
      if (!this.canCreateReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para crear recibos.", "warning");
        return;
      }
      this.importRows = [];
      this.importEffectiveDate = this.nowLocalDateTime();
      this.showImportModal = true;
    },
    closeImportModal() {
      this.showImportModal = false;
      this.importRows = [];
      this.importEffectiveDate = "";
      this.parsingImport = false;
      this.importing = false;
      if (this.$refs.importFileInput) this.$refs.importFileInput.value = "";
    },
    isValidAmount(value) {
      if (value === null || value === undefined) return false;
      const text = String(value).replace(/[$,\s]/g, "").trim();
      if (text === "") return false;
      const parsed = Number(text);
      return !Number.isNaN(parsed);
    },
    rowIsValid(row) {
      return (
        Boolean(row.empleado) &&
        Boolean(row.concepto) &&
        this.isValidAmount(row.monto) &&
        row.employeeExists === true &&
        (!row.unidad || row.unitExists !== false)
      );
    },
    removeImportRow(clientId) {
      this.importRows = this.importRows.filter((row) => row.clientId !== clientId);
    },
    scheduleImportValidation() {
      if (this.importValidationTimer) clearTimeout(this.importValidationTimer);
      this.importValidationTimer = setTimeout(() => {
        this.validateImportRows();
      }, 400);
    },
    async validateImportRows() {
      if (!this.importRows.length) return;
      const rowsSnapshot = this.importRows.map((row) => ({
        clientId: row.clientId,
        empleado: row.empleado,
        unidad: row.unidad,
      }));
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.post(`${apiDirectory}/employee-receipts-import-validate/`, {
          rows: rowsSnapshot.map((row) => ({ empleado: row.empleado, unidad: row.unidad })),
        });
        const results = Array.isArray(response.data?.results) ? response.data.results : [];
        rowsSnapshot.forEach((snapshot, index) => {
          const result = results[index];
          if (!result) return;
          const target = this.importRows.find((row) => row.clientId === snapshot.clientId);
          if (!target) return;
          if (target.empleado === snapshot.empleado) target.employeeExists = result.employee_exists;
          if (target.unidad === snapshot.unidad) target.unitExists = result.unit_exists;
        });
      } catch (error) {
        // Existence flags stay as-is; save-time validation is the final gate.
      }
    },
    async onImportFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append("file", file);
      this.parsingImport = true;
      this.importRows = [];
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.post(`${apiDirectory}/employee-receipts-import-parse/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
        if (!rows.length) {
          Swal.fire("Archivo vacío", "No se encontraron filas de datos en el archivo.", "warning");
        }
        this.importRows = rows.map((row) => ({
          clientId: (this.importClientIdCounter += 1),
          empleado: row.empleado || "",
          concepto: row.concepto || "",
          unidad: row.unidad || "",
          monto: row.monto || "",
          observaciones: row.observaciones || "",
          employeeExists: null,
          unitExists: true,
        }));
        if (this.importRows.length) this.validateImportRows();
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire("Error", errorMessage || "No se pudo leer el archivo.", "error");
      } finally {
        this.parsingImport = false;
      }
    },
    async saveImport() {
      if (!this.canCreateReceipt) {
        Swal.fire("Sin acceso", "No tienes permisos para crear recibos.", "warning");
        return;
      }
      if (!this.importRows.length) {
        Swal.fire("Sin datos", "No hay filas para importar.", "warning");
        return;
      }
      if (!this.importEffectiveDate) {
        Swal.fire("Fecha requerida", "Selecciona la fecha de aplicación para esta carga.", "warning");
        return;
      }
      const invalidRows = this.importRows
        .map((row, index) => ({ row, index }))
        .filter((item) => !this.rowIsValid(item.row));
      if (invalidRows.length) {
        const details = invalidRows
          .map((item) => {
            const problems = [];
            if (!item.row.empleado) problems.push("falta empleado");
            else if (item.row.employeeExists === false) problems.push("el empleado no existe");
            if (!item.row.concepto) problems.push("falta concepto");
            if (!this.isValidAmount(item.row.monto)) problems.push("falta monto");
            if (item.row.unidad && item.row.unitExists === false) problems.push("la unidad no existe");
            return `Fila ${item.index + 1}: ${problems.join(", ")}`;
          })
          .join("<br>");
        Swal.fire({
          icon: "error",
          title: "Hay filas inválidas",
          html: `Corrige los campos obligatorios antes de importar:<br><br>${details}`,
        });
        return;
      }
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const payload = {
          effective_date: this.importEffectiveDate,
          rows: this.importRows.map((row) => ({
            empleado: row.empleado,
            concepto: row.concepto,
            unidad: row.unidad,
            monto: row.monto,
            observaciones: row.observaciones,
          })),
        };
        this.importing = true;
        const response = await axios.post(`${apiDirectory}/employee-receipts-import/`, payload);
        const created = response.data?.created ?? this.importRows.length;
        this.closeImportModal();
        Swal.fire("Importación exitosa", `Se crearon ${created} recibo(s).`, "success");
        this.currentPage = 1;
        await this.queryReceipts();
      } catch (error) {
        const responseData = error.response ? error.response.data : null;
        if (responseData && Array.isArray(responseData.errors) && responseData.errors.length) {
          const details = responseData.errors
            .map((item) => `Fila ${item.row}: ${item.message}`)
            .join("<br>");
          Swal.fire({
            icon: "error",
            title: responseData.error || "Filas inválidas",
            html: details,
          });
        } else {
          const errorMessage = responseData ? (responseData.error || JSON.stringify(responseData)) : error.message;
          Swal.fire("Error", errorMessage || "No se pudieron importar los recibos.", "error");
        }
      } finally {
        this.importing = false;
      }
    },
    async queryReceipts() {
      if (!this.canViewTable) {
        Swal.fire("Sin acceso", "No tienes permisos para ver recibos.", "warning");
        return;
      }
      const payload = {
        employee_ident: this.employeeIdent,
        employee_name: this.employeeName,
        concept_description: this.conceptDescription,
        approved_by: this.approvedBy,
        unit_short_id: this.unitShortId,
        amount_min: this.amountMin,
        amount_max: this.amountMax,
        request_time_start: this.requestTimeStart || "",
        request_time_end: this.requestTimeEnd || "",
        effective_date_start: this.effectiveDateStart || "",
        effective_date_end: this.effectiveDateEnd || "",
        status: this.receiptStatus || "",
      };
      const orderingQS = this.orderingParam();
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const url = `${apiDirectory}/get-employee-receipts-filtered?page=${this.currentPage}&page_size=${this.pageSize}${orderingQS}`;
        const response = await axios.post(url, payload);
        this.queriedReceipts = response.data.results || [];
        this.totalCount = response.data.count || 0;
        this.$nextTick(() => {
          const el = document.getElementById("results-section");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire({ icon: "error", title: "Error generando reporte", text: errorMessage });
      }
    },
  },
};
</script>

<style scoped>
:root {
  --brand: #ffc434;
  --brand-600: #e6b12e;
  --bg: #f7f7f8;
  --card: #ffffff;
  --border: #e8e8ea;
  --muted: #70737a;
  --text: #1f2328;
  --radius-sm: 6px;
  --radius-md: 10px;
  --shadow-1: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.orders-view { background: var(--bg); min-height: 100%; }
.no-access { margin: 16px 0; padding: 12px; border-radius: 10px; border: 1px solid #f3c7c7; background: #fff3f3; color: #9b1c1c; font-weight: 700; }
.nav-bar-operations { position: fixed; top: 0; left: 0; width: 100%; height: 56px; background: var(--brand); z-index: 1000; box-shadow: var(--shadow-1); }
.logo-button { position: absolute; height: 80%; top: 50%; left: 50%; transform: translate(-50%, -50%); cursor: pointer; margin: 0; }
.top-spacer { height: 56px; }
.panel-head { margin: 10px 10px 8px; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between; }
.panel-head h3 { margin: 0; font-size: 14px; color: var(--text); letter-spacing: 0.2px; }
.head-icon { width: 22px; height: 22px; opacity: 0.9; }
.head-actions { display: flex; align-items: center; gap: 10px; }
.filters-card { margin: 0 10px 10px; background: var(--card); border: 1px solid var(--border); border-radius: 8px; box-shadow: var(--shadow-1); padding: 8px; display: grid; grid-template-columns: 1fr; gap: 6px; font-size: 12px; }
.filters-card .row { display: grid; grid-template-columns: 140px 320px 22px 320px; align-items: center; gap: 4px; min-height: 28px; }
.filters-card .row.actions { grid-template-columns: 1fr; margin-top: 2px; }
.filters-card label { color: var(--text); font-size: 12px; white-space: nowrap; }
.filters-card .sep { text-align: center; color: var(--muted); font-size: 11px; }
.filters-card input[type="text"], .filters-card input[type="number"], .filters-card input[type="datetime-local"], .filters-card select {
  height: 26px; padding: 0 8px; border: 1px solid #d2d2d2; border-radius: var(--radius-sm); font-size: 12px; outline: none; background: #fff;
}
.btn-primary { padding: 6px 12px; font-size: 12px; border-radius: 8px; background: var(--brand); border: none; cursor: pointer; box-shadow: var(--shadow-1); }
.btn-primary:hover { background: var(--brand-600); }
.results-card { margin: 0 10px 16px; background: var(--card); border: 1px solid var(--border); border-radius: 8px; box-shadow: var(--shadow-1); overflow: hidden; }
.results-head { padding: 8px 10px; border-bottom: 1px solid var(--border); }
.results-head h2 { font-size: 14px; margin: 0; color: var(--text); }
.table-scroll { overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; font-size: 12px; line-height: 1.15; }
.table thead th { position: sticky; top: 0; z-index: 1; background: #f5f6f8; color: #475163; font-weight: 600; border-bottom: 1px solid var(--border); padding: 6px 8px; white-space: nowrap; cursor: pointer; }
.table tbody td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.table tbody tr:nth-child(even) td { background: #fcfcfd; }
.table tbody tr:hover td { background: #eef3ff; color: #0f172a; }
.row-click { cursor: pointer; }
.pagination { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 6px; padding: 10px; border-top: 1px solid var(--border); }
.pg-btn { background: #fff; border: 1px solid var(--border); border-radius: 8px; padding: 2px 6px; cursor: pointer; box-shadow: var(--shadow-1); }
.pg-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pg-btn img { width: 18px; height: auto; display: block; }
.pg-num { min-width: 28px; text-align: center; padding: 4px 8px; border-radius: 8px; border: 1px solid var(--border); background: #fff; cursor: pointer; user-select: none; }
.pg-num.active { background: var(--brand); border-color: #e0b12b; }

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 16px;
}
.modal-card {
  width: min(900px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-1);
}
.modal-card-lg { width: min(980px, 100%); }
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
}
.modal-head h4 { margin: 0; font-size: 14px; }
.btn-close {
  border: 1px solid var(--border);
  background: #f8f8f8;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
}
.modal-body { padding: 12px; }
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--border);
}
.btn-secondary {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: #f5f5f5;
  cursor: pointer;
}
.btn-danger {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
  border: 1px solid #f0c2c2;
  background: #fdeaea;
  color: #9b1c1c;
  cursor: pointer;
}
.btn-danger:hover { background: #f9d9d9; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
.status-pill { display: inline-block; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }
.status-active { background: #e6f7ea; color: #1a7f37; border: 1px solid #b6e3c1; }
.status-cancelled { background: #fdeaea; color: #9b1c1c; border: 1px solid #f2c2c2; }
.table tbody tr.row-cancelled td { color: #9b1c1c; text-decoration: line-through; text-decoration-color: rgba(155, 28, 28, 0.5); }
.table tbody tr.row-cancelled td .status-pill { text-decoration: none; }
.cancelled-banner {
  background: #fdeaea;
  border: 1px solid #f2c2c2;
  color: #9b1c1c;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 12px;
}
.receipt-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 11px; font-weight: 700; color: var(--text); }
.field input, .field select, .field textarea {
  width: 100%;
  border: 1px solid #d2d2d2;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  background: #fff;
}
.field textarea {
  min-height: 80px;
  resize: vertical;
}
.field-full { grid-column: 1 / -1; }
.employee-search-wrap { position: relative; }
.suggestions-employee {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-1);
  z-index: 20;
  list-style: none;
  margin: 0;
  padding: 4px 0;
}
.suggestions-employee li {
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
}
.suggestions-employee li:hover { background: #f2f6ff; }
.selected-employee {
  color: var(--muted);
  font-size: 11px;
}

.import-disclaimer {
  background: #fffdf3;
  border: 1px solid #f0e3b0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #5b5320;
  line-height: 1.5;
  margin-bottom: 12px;
}
.import-disclaimer code {
  background: #f3f0df;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 11px;
}
.mandatory-text { color: #9b1c1c; font-weight: 700; }
.import-field-row { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
.import-field-row label { font-size: 12px; font-weight: 700; color: var(--text); }
.import-field-row input[type="datetime-local"] {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #d2d2d2;
  border-radius: var(--radius-sm);
  font-size: 12px;
  background: #fff;
}
.import-file-row { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.import-status { font-size: 12px; color: var(--muted); }
.import-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 8px; }
.badge { font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 999px; }
.badge-ok { background: #e6f7ea; color: #1a7f37; border: 1px solid #b6e3c1; }
.badge-bad { background: #fdeaea; color: #9b1c1c; border: 1px solid #f2c2c2; }
.import-hint { font-size: 11px; color: var(--muted); }
.import-empty { font-size: 12px; color: var(--muted); padding: 12px 0; }
.import-preview { max-height: 50vh; overflow: auto; border: 1px solid var(--border); border-radius: 8px; }
.import-preview table td { padding: 3px 5px; }
.import-preview table input {
  width: 100%;
  min-width: 90px;
  border: 1px solid #d2d2d2;
  border-radius: 5px;
  padding: 4px 6px;
  font-size: 12px;
  background: #fff;
}
.import-preview .cell-error { border-color: #d64545; background: #fff5f5; }
.import-preview .cell-note { display: block; color: #9b1c1c; font-size: 10px; margin-top: 2px; }
.import-preview tr.row-valid td { background: #f2fbf4; }
.import-preview tr.row-invalid td { background: #fdf1f1; }
.btn-remove-row {
  border: 1px solid #f2c2c2;
  background: #fdeaea;
  color: #9b1c1c;
  border-radius: 5px;
  padding: 2px 7px;
  cursor: pointer;
  font-size: 11px;
}

@media (max-width: 1200px) {
  .filters-card .row { grid-template-columns: 120px minmax(180px, 1fr) 18px minmax(180px, 1fr); }
}
@media (max-width: 900px) {
  .filters-card .row { grid-template-columns: 120px 1fr; }
  .filters-card .sep, .filters-card .row > .cell { display: none; }
  .receipt-grid { grid-template-columns: 1fr; }
}
</style>
